import { useState } from "react";
import styles from "./LoginForm.module.css";
import { KeyRound, Lock, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPassword } from "../../services/api";
import T from '../T';

const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.code || !formData.newPassword || !formData.confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    const email = localStorage.getItem("forgot_password_email");
    if (!email) {
      toast.error(
        "Session expired. Please restart the forgot password process.",
      );
      setTimeout(() => navigate("/forgot-password"), 2000);
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(formData.code, email, formData.newPassword, formData.confirmPassword);

      // Clean up the stored email
      localStorage.removeItem("forgot_password_email");

      toast.success("Password reset successfully! You can now log in.");

      // Redirect to login after a brief delay
      setTimeout(() => navigate("/staff"), 1500);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Reset password error:", error);
      }
      toast.error(
        error instanceof Error
          ? error.message
          : "Password reset failed. Please try again.",
      );

      // Redirect to login so user can retry via Forgot Password
      setTimeout(() => navigate("/staff"), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}><T>Reset Password</T></h2>
        <p className={styles.subtitle}>
          <T>Enter the code sent to your email and choose a new password.</T>
        </p>
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* Reset Code */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <KeyRound size={16} />
          </div>
          <input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleChange}
            placeholder="Enter reset code"
            className={styles.input}
            required
            disabled={isLoading}
          />
        </div>

        {/* New Password */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <Lock size={16} />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            placeholder="New password"
            className={styles.input}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            className={styles.eyeToggle}
            onClick={() => setShowPassword(!showPassword)}
            disabled={isLoading}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Confirm Password */}
        <div className={styles.inputGroup}>
          <div className={styles.iconWrapper}>
            <Lock size={16} />
          </div>
          <input
            type={showPassword ? "text" : "password"}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Confirm password"
            className={styles.input}
            required
            disabled={isLoading}
          />
        </div>

        <button
          type="submit"
          className={styles.signInButton}
          disabled={isLoading}
        >
          {isLoading ? (
            <T>Resetting...</T>
          ) : (
            <>
              <T>Reset Password</T> <span className={styles.arrow}>→</span>
            </>
          )}
        </button>

        <div
          style={{
            marginTop: "1.5rem",
            textAlign: "center",
            fontSize: "0.9rem",
            color: "#6b7280",
          }}
        >
          <span
            onClick={() => navigate("/staff")}
            style={{ color: "#f5a623", cursor: "pointer", fontWeight: "500" }}
          >
            <T>Back to Login</T>
          </span>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
